import React, { useCallback, useMemo } from 'react';
import { usePhoneInput } from '../../hooks/usePhoneInput';
import { useThemeColors } from '../../providers/ThemeProvider';
import { cn } from '../../utils/cn';

interface CountryCode {
	code: string;
	dialCode: string;
	label: string;
}

// Only US/CA (+1) supported for now — international formatting requires libphonenumber-js
const COUNTRY_CODES: Array<CountryCode> = [
	{ code: 'US', dialCode: '+1', label: 'US/CA +1' },
];

interface PhoneInputProps {
	/** Raw phone value — stored in E.164 format (e.g., "+12485551234") */
	value: string;
	/** Called with the E.164 formatted value when the user types */
	onChange: (value: string) => void;
	/** Placeholder text for the number portion */
	placeholder?: string;
	/** Input name attribute */
	name?: string;
	/** Disable the input */
	disabled?: boolean;
	/** Show error styling */
	error?: boolean;
	/** Size variant */
	size?: 'xs' | 'sm' | 'md' | 'lg';
	/** Additional CSS classes for the container */
	className?: string;
	/** Default country code (default: 'US') */
	defaultCountry?: string;
}

/**
 * Parses an E.164 value into country dial code and local number.
 * e.g., "+12485551234" → { dialCode: "+1", localNumber: "2485551234" }
 */
function parseE164(value: string): { dialCode: string; localNumber: string } {
	if (!value.startsWith('+')) {
		// No country code — assume US
		return { dialCode: '+1', localNumber: value.replace(/\D/g, '') };
	}

	// Try matching known dial codes (longest first to avoid ambiguity)
	const sortedCodes = [...COUNTRY_CODES].sort(
		(a, b) => b.dialCode.length - a.dialCode.length,
	);

	for (const country of sortedCodes) {
		if (value.startsWith(country.dialCode)) {
			const localNumber = value
				.slice(country.dialCode.length)
				.replace(/\D/g, '');

			return { dialCode: country.dialCode, localNumber };
		}
	}

	// Unknown country code — extract first few digits as dial code
	const digits = value.replace(/\D/g, '');

	return { dialCode: '+1', localNumber: digits };
}

/**
 * Standardized phone number input with country code selector.
 *
 * - Country code dropdown (defaults to US +1)
 * - Formats the local number as user types: (248) 555-1234
 * - Stores in E.164 format: +12485551234
 * - Handles pasting full E.164 numbers
 *
 * Usage:
 * ```tsx
 * <PhoneInput
 *   value={formData.phone}
 *   onChange={(val) => handleChange('phone', val)}
 * />
 * ```
 */
export const PhoneInput = React.forwardRef<HTMLInputElement, PhoneInputProps>(
	(
		{
			value,
			onChange,
			placeholder = '(555) 123-4567',
			name,
			disabled,
			error,
			size = 'md',
			className,
			defaultCountry = 'US',
		},
		ref,
	) => {
		const colors = useThemeColors();

		// Parse current value to extract country code and local number
		const { dialCode, localNumber } = useMemo(() => {
			if (value == null || value === '') {
				const defaultDialCode =
					COUNTRY_CODES.find((c) => c.code === defaultCountry)?.dialCode ??
					'+1';

				return { dialCode: defaultDialCode, localNumber: '' };
			}

			return parseE164(value);
		}, [value, defaultCountry]);

		// When local number changes, combine with dial code for E.164 output
		const handleLocalNumberChange = useCallback(
			(rawLocalNumber: string) => {
				const digits = rawLocalNumber.replace(/\D/g, '');

				if (digits === '') {
					onChange('');

					return;
				}

				onChange(`${dialCode}${digits}`);
			},
			[dialCode, onChange],
		);

		// When country code changes, reformat with new dial code
		const handleDialCodeChange = useCallback(
			(newDialCode: string) => {
				if (localNumber === '') {
					onChange('');

					return;
				}

				onChange(`${newDialCode}${localNumber}`);
			},
			[localNumber, onChange],
		);

		// Use the phone input hook for formatting the local number portion
		const phoneInput = usePhoneInput(localNumber, handleLocalNumberChange);

		const sizeClasses = {
			xs: 'text-xs py-1',
			sm: 'text-sm py-1.5',
			md: 'text-sm py-2',
			lg: 'text-base py-2.5',
		};

		const selectSizeClasses = {
			xs: 'text-xs py-1 pr-1 pl-2',
			sm: 'text-sm py-1.5 pr-1 pl-2',
			md: 'text-sm py-2 pr-1 pl-2',
			lg: 'text-base py-2.5 pr-1 pl-3',
		};

		return (
			<div className={cn('flex', className)}>
				{/* Country code display — locked to +1 until international formatting is supported */}
				<div
					className={cn(
						'flex items-center rounded-l-md border border-r-0 whitespace-nowrap',
						selectSizeClasses[size],
						error ? 'border-danger-tint' : colors.classes.borderDefault,
						disabled ? 'opacity-50' : '',
						colors.classes.bgDefault,
						colors.classes.textSecondary,
					)}
				>
					+1
				</div>

				{/* Phone number input */}
				<input
					ref={ref}
					type="tel"
					name={name}
					value={phoneInput.value}
					onChange={phoneInput.onChange}
					placeholder={placeholder}
					disabled={disabled}
					className={cn(
						'flex-1 rounded-r-md border px-3',
						'focus:outline-none focus:ring-2 focus:ring-inset',
						sizeClasses[size],
						error
							? 'border-danger-tint focus:ring-danger'
							: cn(colors.classes.borderDefault, 'focus:ring-accent'),
						disabled ? 'opacity-50 cursor-not-allowed' : '',
						colors.classes.bgDefault,
						colors.classes.textPrimary,
					)}
				/>
			</div>
		);
	},
);

PhoneInput.displayName = 'PhoneInput';
